import { motion } from "framer-motion";
import { ShieldCheck, Award, Hammer, Heart, ArrowRight, Sparkles } from "lucide-react";

const FOUNDER_PHOTO =
  "https://customer-assets.emergentagent.com/job_facade-visualizer/artifacts/d4x33bkf_5BFCF792-6700-4750-AECB-8342A232340D.png";

const VALUES = [
  {
    icon: Hammer,
    title: "Entreprise familiale",
    text: "Plus de 25 ans d'expérience transmis de génération en génération. Un seul interlocuteur du devis à la réception.",
  },
  {
    icon: ShieldCheck,
    title: "Garantie décennale",
    text: "Couverture totale pendant 10 ans sur tous les travaux de façade et de rénovation extérieure.",
  },
  {
    icon: Award,
    title: "Matériaux nobles",
    text: "Enduits projetés haut de gamme, pierre reconstituée, briques de récupération sélectionnées.",
  },
  {
    icon: Heart,
    title: "Finitions soignées",
    text: "L'œil du détail sur chaque chantier. Nettoyage complet du site à la livraison.",
  },
];

const STATS = [
  { value: "25+", label: "Années d'expérience" },
  { value: "500+", label: "Chantiers réalisés" },
  { value: "5,0", label: "Note clients Google" },
  { value: "10 ans", label: "Garantie décennale" },
];

export default function About() {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative py-24 md:py-32 bg-[#050505] border-y border-white/5 overflow-hidden"
    >
      <div
        className="absolute inset-0 opacity-30 pointer-events-none"
        style={{
          background:
            "radial-gradient(circle at 20% 30%, rgba(255,90,0,0.15) 0%, transparent 50%)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start mb-20">
          {/* Photo */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-5"
          >
            <div className="relative rounded-3xl overflow-hidden border border-white/10 group">
              <img
                src={FOUNDER_PHOTO}
                alt="Siège et flotte de véhicules GMC Rénovation Façade"
                loading="lazy"
                decoding="async"
                className="w-full aspect-square object-cover transition-transform duration-700 group-hover:scale-105"
                data-testid="about-founder-photo"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent pointer-events-none" />
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <div className="eyebrow mb-2">Entreprise familiale</div>
                <div className="font-display text-3xl text-white leading-none mb-1">
                  Notre siège & flotte
                </div>
                <div className="text-sm text-neutral-300">25+ ans d&apos;expertise façade</div>
              </div>
            </div>
          </motion.div>

          {/* Story */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="lg:col-span-7"
          >
            <div className="eyebrow mb-4 inline-flex items-center gap-2">
              <Sparkles size={12} />
              <span>À propos</span>
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-light leading-tight mb-8">
              Plus de 25 ans
              <br />
              <em className="text-[#FF5A00] not-italic font-normal">
                au service de votre façade
              </em>
            </h2>
            <div className="space-y-5 text-neutral-300 text-lg leading-relaxed">
              <p>
                <strong className="text-white">GMC Rénovation Façade</strong> est une{" "}
                <strong className="text-white">entreprise familiale</strong> implantée à
                Valenciennes. Depuis plus de <strong className="text-white">25 ans</strong>,
                nous redonnons ses lettres de noblesse à chaque maison du Nord, qu&apos;elle
                soit en brique ancienne ou en construction neuve.
              </p>
              <p>
                Le savoir-faire se transmet ici de génération en génération. De
                l&apos;enduit projeté aux pièces sur mesure (fours à pizza, appliques
                architecturales, imitation pierre), nous mettons la même exigence :{" "}
                <strong className="text-white">
                  finitions impeccables, matériaux nobles, respect du planning
                </strong>
                .
              </p>
              <p className="text-base text-neutral-400">
                « Une façade, c&apos;est la première impression de votre maison. Elle mérite
                qu&apos;on lui consacre tout notre savoir-faire familial. »
              </p>
            </div>

            <a
              href="#contact"
              data-testid="about-cta"
              className="btn-primary mt-10 inline-flex"
            >
              Discutons de votre projet <ArrowRight size={16} />
            </a>
          </motion.div>
        </div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-20"
          data-testid="about-stats"
        >
          {STATS.map((s) => (
            <div
              key={s.label}
              className="glass rounded-2xl p-6 md:p-8 text-center"
            >
              <div className="font-display text-4xl md:text-5xl text-[#FF5A00] mb-2 leading-none">
                {s.value}
              </div>
              <div className="text-xs md:text-sm uppercase tracking-[0.15em] text-neutral-400">
                {s.label}
              </div>
            </div>
          ))}
        </motion.div>

        {/* Values */}
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-neutral-500 mb-8">
            Pourquoi nous choisir
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {VALUES.map((v, i) => (
              <motion.div
                key={v.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                data-testid={`about-value-${i}`}
                className="glass rounded-2xl p-6 hover:border-white/30 transition-all group"
              >
                <div className="w-11 h-11 rounded-full bg-[#FF5A00]/15 flex items-center justify-center text-[#FF5A00] mb-4 group-hover:scale-110 transition-transform">
                  <v.icon size={18} />
                </div>
                <h3 className="font-display text-xl text-white mb-2">{v.title}</h3>
                <p className="text-sm text-neutral-400 leading-relaxed">{v.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
